// Activity engine (pure): calendar math + scoreboard counts (T-ACT-001).
import { leadValueOf } from './leadConstants';

const DAY = 86400000;

export function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

// Week number counting from Jan 1, Sunday-aligned (Week 1 = the week of Jan 1).
export function weekNumber(date) {
  const d = new Date(date);
  const jan1 = new Date(d.getFullYear(), 0, 1);
  const dayOfYear = Math.floor((startOfDay(d) - startOfDay(jan1)) / DAY);
  return Math.floor((dayOfYear + jan1.getDay()) / 7) + 1;
}

// 6×7 month grid (Sunday-start). Returns rows of {date, inMonth, week}.
export function monthGrid(year, month) {
  const first = new Date(year, month, 1);
  const start = new Date(year, month, 1 - first.getDay()); // back to Sunday
  const rows = [];
  for (let w = 0; w < 6; w += 1) {
    const cells = [];
    for (let i = 0; i < 7; i += 1) {
      const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + w * 7 + i);
      cells.push({ date, inMonth: date.getMonth() === month, week: weekNumber(date) });
    }
    rows.push(cells);
  }
  return rows;
}

// The 7 days of the week (Sunday-start) containing `date`.
export function weekDays(date) {
  const d = new Date(date);
  const sunday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - d.getDay());
  return Array.from({ length: 7 }, (_, i) =>
    new Date(sunday.getFullYear(), sunday.getMonth(), sunday.getDate() + i));
}

// Local yyyy-mm-dd key (NOT toISOString — that shifts the day across timezones).
export function dayKey(ms) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Bucket leads by day using a ms timestamp field (default nextDate).
export function groupByDay(leads, field = 'nextDate') {
  const map = {};
  for (const l of leads || []) {
    const ms = l[field];
    if (typeof ms !== 'number') continue;
    const key = dayKey(ms);
    (map[key] ||= []).push(l);
  }
  return map;
}

const CLOSED = new Set(['Won', 'Lost']);

// Leading-indicator tiles from the leads a user can see.
export function computeScoreboard(leads, now = Date.now()) {
  const today = startOfDay(now);
  const tomorrow = today + DAY;
  const weekStart = startOfDay(weekDays(now)[0]);
  const weekEnd = weekStart + 7 * DAY;
  const ref = new Date(now);
  const monthStart = startOfDay(new Date(ref.getFullYear(), ref.getMonth(), 1));
  const monthEnd = startOfDay(new Date(ref.getFullYear(), ref.getMonth() + 1, 1));
  const s = {
    followupsToday: 0, followupsOverdue: 0, followupsThisWeek: 0, followupsThisMonth: 0, won: 0, wonValue: 0, activePipeline: 0,
  };
  for (const l of leads || []) {
    const nd = l.nextDate;
    const open = !CLOSED.has(l.stage);
    if (open) s.activePipeline += 1;
    if (typeof nd === 'number') {
      if (nd >= today && nd < tomorrow) s.followupsToday += 1;
      if (nd < today && open) s.followupsOverdue += 1;
      if (nd >= weekStart && nd < weekEnd) s.followupsThisWeek += 1;
      if (nd >= monthStart && nd < monthEnd) s.followupsThisMonth += 1;
    }
    if (l.stage === 'Won') { s.won += 1; s.wonValue += leadValueOf(l); }
  }
  return s;
}

// The actual lead lists behind the four My Day tiles (BUG-018: tiles open a
// filtered list). Counts equal computeScoreboard's by construction:
//  - overdue: an open lead whose nextDate is before today
//  - today: a lead whose nextDate is today
//  - service: a customer whose nextServiceAt falls in the next 7 days
//  - touches: logged-today has no event source yet → always empty (count 0)
export function myDayTiles(leads, now = Date.now()) {
  const today = startOfDay(now);
  const tomorrow = today + DAY;
  const weekEnd = today + 7 * DAY;
  const overdue = [];
  const dueToday = [];
  const service = [];
  for (const l of leads || []) {
    const nd = l.nextDate;
    const open = !CLOSED.has(l.stage);
    if (typeof nd === 'number') {
      if (nd >= today && nd < tomorrow) dueToday.push(l);
      if (nd < today && open) overdue.push(l);
    }
    if (l.lifecycle === 'customer' && typeof l.nextServiceAt === 'number'
      && l.nextServiceAt >= today && l.nextServiceAt < weekEnd) service.push(l);
  }
  return {
    overdue, today: dueToday, service, touches: [],
  };
}

// 3-block day agenda for My Day's Day view (Phase 4, mirrors the mockup calDay):
// Overdue · Due <cursor day> · Later this week — relative to the cursor date so
// the day arrows still work (cursor = today gives the classic "today" agenda).
export function dayBlocks(leads, cursor = Date.now()) {
  const day = startOfDay(cursor);
  const nextDay = day + DAY;
  const weekEnd = day + 7 * DAY;
  const overdue = [];
  const today = [];
  const later = [];
  for (const l of leads || []) {
    const nd = l.nextDate;
    if (typeof nd !== 'number') continue;
    const open = !CLOSED.has(l.stage);
    if (nd >= day && nd < nextDay) today.push(l);
    else if (nd < day && open) overdue.push(l);
    else if (nd >= nextDay && nd < weekEnd) later.push(l);
  }
  return { overdue, today, later };
}

// Per-territory rollup (territory ≈ a rep's patch). Territories are passed in
// (tenant-aware) rather than read from a constant, so each tenant rolls up its own.
export function scoreboardByTerritory(leads, territories = [], now = Date.now()) {
  return (territories || []).map((t) => ({
    id: t.id,
    label: t.label ?? t.id,
    ...computeScoreboard((leads || []).filter((l) => l.territoryId === t.id), now),
  }));
}

// Per-SOURCE rollup (v1.1 — where leads originate: FB/IG/TikTok chatbot vs
// outbound/manual). Bucket = sourcePlatform for chatbot leads (`source` set;
// legacy inbound rows without a platform count as facebook), else "manual".
// Two rates per Jorwin (2026-07-21, "show both"):
//   closeRate  = won ÷ (won + lost)  — decided deals only
//   conversion = won ÷ total         — end-to-end yield
// Both are null when their denominator is 0 (UI renders "—"). Empty buckets are
// omitted, so a tenant with no chatbot (DoR-33: Actiserve) yields only "manual"
// — the Scoreboard hides the card entirely in that case.
const SOURCE_LABELS = {
  facebook: 'Facebook', instagram: 'Instagram', tiktok: 'TikTok', manual: 'Outbound / manual',
};
const SOURCE_ORDER = ['facebook', 'instagram', 'tiktok', 'manual'];
export function scoreboardBySource(leads) {
  const buckets = {};
  for (const l of leads || []) {
    const key = l.source ? (l.sourcePlatform || 'facebook') : 'manual';
    const b = (buckets[key] ||= { total: 0, won: 0, lost: 0 });
    b.total += 1;
    if (l.stage === 'Won') b.won += 1;
    else if (l.stage === 'Lost') b.lost += 1;
  }
  return SOURCE_ORDER.filter((k) => buckets[k]).map((key) => {
    const { total, won, lost } = buckets[key];
    return {
      key,
      label: SOURCE_LABELS[key] ?? key,
      total,
      won,
      lost,
      closeRate: (won + lost) > 0 ? won / (won + lost) : null,
      conversion: total > 0 ? won / total : null,
    };
  });
}
